import { useState, useEffect, useRef, useCallback } from 'react';
import { useImageUpload } from '@/hooks/domains/storage/useImageUpload';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import { readFile } from '@/utils/cropImage';

interface UseCoverImageUploadProps {
  coverImage: string;
  onCoverImageChange: (url: string) => void;
}

interface UseCoverImageUploadReturn {
  coverImagePreview: string;
  isDraggingCover: boolean;
  isUploadingCover: boolean;
  coverImageError: string | null;
  coverImageInputRef: React.RefObject<HTMLInputElement>;
  handleCoverImageClick: () => void;
  handleCoverImageDragOver: (e: React.DragEvent) => void;
  handleCoverImageDragLeave: (e: React.DragEvent) => void;
  handleCoverImageDrop: (e: React.DragEvent) => void;
  handleRemoveCoverImage: () => void;
  handleCoverImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showCropModal: boolean;
  cropModalImageSrc: string;
  handleCropComplete: (croppedBlob: Blob) => void;
  handleCropCancel: () => void;
}

export const useCoverImageUpload = ({
  coverImage,
  onCoverImageChange
}: UseCoverImageUploadProps): UseCoverImageUploadReturn => {
  const { user } = useAuth();
  const coverImageInputRef = useRef<HTMLInputElement>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>("");
  const [isDraggingCover, setIsDraggingCover] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropModalImageSrc, setCropModalImageSrc] = useState('');
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  // Cover image upload hook
  const {
    uploading: isUploadingCover,
    imagePreviews: coverImagePreviews,
    currentError: coverImageError,
    handleImageUpload: uploadImageToStorage,
  } = useImageUpload({
    user,
    onImageUrlChange: (urls) => {
      if (urls.length > 0) {
        onCoverImageChange(urls[0]);
        setCoverImagePreview(urls[0]);
      }
    },
    bucketName: "case-study-images",
  });

  // Initialize cover image preview
  useEffect(() => {
    if (coverImage) {
      setCoverImagePreview(coverImage);
    }
  }, [coverImage]);

  // Cover image upload handlers
  const handleCoverImageClick = () => {
    if (coverImageInputRef.current) {
      coverImageInputRef.current.click();
    }
  };

  const handleCoverImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingCover(true);
  };

  const handleCoverImageDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingCover(false);
  };

  // Handle file selection - show crop modal instead of uploading immediately
  const handleCoverImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log('[useCoverImageUpload] Files selected:', files?.length);
    if (!files || files.length === 0) return;

    const file = files[0];
    console.log('[useCoverImageUpload] File:', file.name, file.type, file.size);
    if (!file.type.startsWith('image/')) return;

    try {
      // Read file as data URL to display in crop modal
      console.log('[useCoverImageUpload] Reading file...');
      const imageDataUrl = await readFile(file);
      console.log('[useCoverImageUpload] File read successfully, data URL length:', imageDataUrl.length);
      setOriginalFile(file);
      setCropModalImageSrc(imageDataUrl);
      setShowCropModal(true);
      console.log('[useCoverImageUpload] Modal state set to true');

      // Reset the input so the same file can be selected again
      if (coverImageInputRef.current) {
        coverImageInputRef.current.value = '';
      }
    } catch (error) {
      console.error('[useCoverImageUpload] Error reading file:', error);
    }
  }, []);

  const handleCoverImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingCover(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        // Create a synthetic event for the file
        const syntheticEvent = {
          target: { files: [file] }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleCoverImageUpload(syntheticEvent);
      }
    }
  };

  // Handle crop complete - upload the cropped image
  const handleCropComplete = useCallback(async (croppedBlob: Blob) => {
    try {
      // Convert blob to File object with original filename
      const filename = originalFile?.name || 'cover-image.jpg';
      const croppedFile = new File([croppedBlob], filename, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Close the crop modal immediately
      setShowCropModal(false);
      setCropModalImageSrc('');
      setOriginalFile(null);

      // Upload the cropped image in the background
      const syntheticEvent = {
        target: { files: [croppedFile] }
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await uploadImageToStorage(syntheticEvent);
    } catch (error) {
      console.error('Error uploading cropped image:', error);
    }
  }, [originalFile, uploadImageToStorage]);

  // Handle crop cancel
  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    setCropModalImageSrc('');
    setOriginalFile(null);
  }, []);

  const handleRemoveCoverImage = () => {
    onCoverImageChange("");
    setCoverImagePreview("");
  };

  return {
    coverImagePreview,
    isDraggingCover,
    isUploadingCover,
    coverImageError,
    coverImageInputRef,
    handleCoverImageClick,
    handleCoverImageDragOver,
    handleCoverImageDragLeave,
    handleCoverImageDrop,
    handleRemoveCoverImage,
    handleCoverImageUpload,
    showCropModal,
    cropModalImageSrc,
    handleCropComplete,
    handleCropCancel,
  };
};
